import { AddSecureDocModel, mapToSecureDocUpdateInfo, SecureDocModel, SecureDocUpdateInfoModel } from "../../models/secure-docs.model";
import { FileTypes } from "../../models/media.models";
import { useEffect, useRef, useState } from "react";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import { appNote, buttonLabels, ToasterTexts } from "../shared/constants";
import { FalkonDocUploadIcon, HidePassword, NewInfoCircleIcon, PDFIcon, ShowPassword } from "../../icon";
import {
    Autocomplete,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    TextField,
    FormHelperText,
    Tooltip,
} from "@mui/material";
import ButtonComponent from "../shared/button/button.component";
import { SecureDocsService } from "../../services/SecureDocsService";
import { UserService } from "../../services/UserService";
import { ProfileModel } from "../../models/profile.model";
import * as selector from "../../selectors/selectors";
import { useSelector } from "react-redux";
import { AppRoutes } from "../routes/route.path";
import { useNavigate } from "react-router-dom";
import { validateForm } from "../shared/form-validation";
import { UtilityService } from "../../services/UtilityService";

export interface RecipientFormData {
    recipient_id: string;
    email: string;
    order_number: number;
    name?: string;
    phone?: string;
    type?: string;
    is_completed?: boolean;
    is_invite_sent?: boolean;
}

export interface PreservedFormData {
    messageText?: string;
    reminderDays?: number;
    passwordProtect?: boolean;
    password?: string;
    completeInOrder?: boolean;
}

interface Props {
    nextHandler: (
        emails?: RecipientFormData[],
        fileID?: string,
        formData?: PreservedFormData
    ) => void;
    isUpdateMode?: boolean;
    fileID?: string;
    preservedRecipients?: RecipientFormData[];
    preservedFormData?: PreservedFormData;
    pdfName?: string | null;
    pdfType?: string | null;
    pdfBase64?: string | null;
}

interface Recipient {
    id: number;
    name: string;
    email: string;
    phone_number: string;
    type: string;
    order_number: string;
    is_completed: boolean;
    is_invite_sent: boolean;
    otpinfo: OtpInfo;
}

interface OtpInfo {
    expires_at: string;
    otp: string;
}

const AddRecipientComponent: React.FC<Props> = (props) => {
    const maxRecipients = 5;
    const borderColors = ["#156fb3", "#7A14FF", "#00C48C", "#0091FF", "#FF647C"];
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage();
    const [fileName, setFileName] = useState<string>("");
    const [fileType, setFileType] = useState<string>("");
    const [fileBase64, setFileBase64] = useState<string>("");
    const navigate = useNavigate();

    const [completeInOrder, setCompleteInOrder] = useState<boolean>(props.preservedFormData?.completeInOrder || false);
    const [reminderValue, setReminderValue] = useState<string>(
        props.preservedFormData?.reminderDays ? `${props.preservedFormData.reminderDays} day${props.preservedFormData.reminderDays > 1 ? 's' : ''}` : "1 day"
    );
    const [protectPassword, setProtectPassword] = useState<boolean>(props.preservedFormData?.passwordProtect || false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [password, setPassword] = useState<string>(props.preservedFormData?.password || "");
    const [message, setMessage] = useState<string>(props.preservedFormData?.messageText || "Please review and sign the document at your earliest convenience.");

    const [updateData, setUpdateData] = useState<AddSecureDocModel | null>(null);
    const [isLoadingUpdateData, setIsLoadingUpdateData] = useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const otpMethods: string[] = ["Email OTP", "SMS OTP"];
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [recipients, setRecipients] = useState<Recipient[]>(
        props.preservedRecipients && props.preservedRecipients.length > 0
            ? props.preservedRecipients.map((recipient, index) => ({
                id: index + 1,
                name: recipient.name || recipient.email.split('@')[0],
                email: recipient.email,
                phone_number: recipient.phone || "",
                type: recipient.type || "Email OTP",
                order_number: recipient.order_number.toString(),
                is_completed: recipient.is_completed || false,
                is_invite_sent: recipient.is_invite_sent || false,
                otpinfo: {
                    expires_at: "",
                    otp: "",
                }
            }))
            : [{
                id: 1,
                phone_number: "",
                type: "",
                email: "",
                is_completed: false,
                is_invite_sent: false,
                name: "",
                order_number: "1",
                otpinfo: {
                    expires_at: "",
                    otp: "",
                }
            }]
    );

    const [errors, setErrors] = useState<{
        message: string;
        password: string;
        recipients: Array<{
            name: string;
            email: string;
            phonenumber: string;
            otptype: string;
        }>;
    }>({
        message: "",
        password: "",
        recipients: recipients.map(() => ({
            name: "",
            email: "",
            phonenumber: "",
            otptype: "",
        })),
    });

    const getRecipientColor = (orderNumber: number): string => {
        return borderColors[(orderNumber - 1) % borderColors.length];
    };

    useEffect(() => {
        if (props.preservedFormData) {
            setMessage(props.preservedFormData.messageText || "Please review and sign the document at your earliest convenience.");
            setReminderValue(
                props.preservedFormData.reminderDays
                    ? `${props.preservedFormData.reminderDays} day${props.preservedFormData.reminderDays > 1 ? 's' : ''}`
                    : "1 day"
            );
            setProtectPassword(props.preservedFormData.passwordProtect || false);
            setPassword(props.preservedFormData.password || "");
            setCompleteInOrder(props.preservedFormData.completeInOrder || false);
        }
    }, [props.preservedFormData]);

    useEffect(() => {
        if (props.preservedRecipients && props.preservedRecipients.length > 0 && !dataLoaded) {
            const preservedData: Recipient[] = props.preservedRecipients.map((recipient, index) => ({
                id: index + 1,
                name: recipient.name || recipient.email.split('@')[0],
                email: recipient.email,
                phone_number: recipient.phone || "",
                type: recipient.type || "Email OTP",
                order_number: recipient.order_number.toString(),
                is_completed: recipient.is_completed || false,
                is_invite_sent: recipient.is_invite_sent || false,
                otpinfo: {
                    expires_at: "",
                    otp: "",
                }
            }));

            setRecipients(preservedData);
            setErrors(prev => ({
                ...prev,
                recipients: preservedData.map(() => ({
                    name: "",
                    email: "",
                    phonenumber: "",
                    otptype: "",
                }))
            }));
            setDataLoaded(true);
        }
    }, [props.preservedRecipients, dataLoaded]);

    useEffect(() => {
        if (props.isUpdateMode === true && props.fileID) {
            setIsLoadingUpdateData(true);
            SecureDocsService.getSignedSecureDocUpdateInfo(props.fileID)
                .then((res) => {
                    const mappedData = mapToSecureDocUpdateInfo(res.data);
                    setUpdateData(mappedData);
                    populateFormWithUpdateData(mappedData);
                })
                .catch((error) => {
                    setToast("Failed to load document data", ToasterType.DANGER);
                })
                .finally(() => {
                    setIsLoadingUpdateData(false);
                    setDataLoaded(true);
                });
        }
    }, [props.isUpdateMode, props.fileID]);

    const populateFormWithUpdateData = (data: SecureDocUpdateInfoModel) => {
        if (data.FileList && data.FileList.length > 0) {
            const file = data.FileList[0];
            setFileName(file.FileName);
            setFileType(file.FileType);
            setFileBase64(file.URL || "");
        }
        setMessage(data.Message || "Please review and sign the document at your earliest convenience.");
        if (data.Recipients && data.Recipients.length > 0) {
            const mappedRecipients: Recipient[] = data.Recipients.map((recipient, index) => ({
                id: index + 1,
                name: recipient.Name,
                email: recipient.Email,
                phone_number: recipient.PhoneNumber,
                type: recipient.Type,
                order_number: recipient.OrderNumber.toString(),
                is_completed: recipient.IsCompleted,
                is_invite_sent: recipient.IsInviteSent,
                otpinfo: {
                    expires_at: recipient.OTPInfo.ExpiresAt,
                    otp: recipient.OTPInfo.OTP,
                }
            }));
            setRecipients(mappedRecipients);
            setErrors(prev => ({
                ...prev,
                recipients: mappedRecipients.map(() => ({
                    name: "",
                    email: "",
                    phonenumber: "",
                    otptype: "",
                }))
            }));
        }
        setCompleteInOrder(data.CompleteInOrder || false);
        if (data.Settings) {
            setProtectPassword(data.Settings.IsPasswordProtected || false);
            setPassword(data.Settings.Password || "");
            setReminderValue(data.Settings.ReminderCount ? `${data.Settings.ReminderCount} day${data.Settings.ReminderCount > 1 ? 's' : ''}` : "1 day");
        }
    };

    useEffect(() => {
        if (!props.isUpdateMode && !dataLoaded) {

            if (props.pdfName && props.pdfType && props.pdfBase64) {
                setFileName(props.pdfName);
                setFileType(props.pdfType);
                setFileBase64(props.pdfBase64);
            }
            setDataLoaded(true);
        }
    }, [props.isUpdateMode, props.pdfName, props.pdfType, props.pdfBase64, dataLoaded]);

    const handleAddRecipient = () => {
        if (recipients.length < maxRecipients) {
            setRecipients([
                ...recipients,
                {
                    id: recipients.length + 1,
                    phone_number: "",
                    type: "",
                    email: "",
                    is_completed: false,
                    is_invite_sent: false,
                    name: "",
                    order_number: (recipients.length + 1).toString(),
                    otpinfo: {
                        expires_at: "",
                        otp: "",
                    }
                },
            ]);
            setErrors(prev => ({
                ...prev,
                recipients: [...prev.recipients, {
                    name: "",
                    email: "",
                    phonenumber: "",
                    otptype: "",
                }]
            }));
        }
    };

    const handleRemoveRecipient = (recipientId: number) => {
        const recipientToRemove = recipients.find(r => r.id === recipientId);
        if (props.isUpdateMode && (recipientToRemove?.is_completed || recipientToRemove?.is_invite_sent)) {
            setToast(ToasterTexts.SECURE_DOC_CANNOT_EDIT_RECIPIENTS, ToasterType.DANGER);
            return;
        }
        if (recipients.length > 1) {
            const filteredRecipients = recipients.filter((recipient) => recipient.id !== recipientId);
            const reorderedRecipients = filteredRecipients.map((recipient, index) => ({
                ...recipient,
                order_number: (index + 1).toString()
            }));
            setRecipients(reorderedRecipients);
            const recipientIndex = recipients.findIndex(r => r.id === recipientId);
            setErrors(prev => ({
                ...prev,
                recipients: prev.recipients.filter((_, index) => index !== recipientIndex)
            }));
        }
    };

    const onClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleRecipientChange = (
        id: number,
        field: keyof Recipient,
        value: string
    ) => {
        setRecipients((prevRecipients) =>
            prevRecipients.map((recipient) =>
                recipient.id === id ? { ...recipient, [field]: value } : recipient
            )
        );

        const recipientIndex = recipients.findIndex(r => r.id === id);
        if (recipientIndex !== -1) {
            let validationKey = '';
            let validationValue = value;

            switch (field) {
                case 'name':
                    validationKey = 'name';
                    break;
                case 'email':
                    validationKey = 'email';
                    break;
                case 'phone_number':
                    validationKey = 'phonenumber';
                    break;
                case 'type':
                    validationKey = 'otptype';
                    break;
            }

            if (validationKey) {
                validateForm({ [validationKey]: validationValue }, (fieldErrors) => {
                    setErrors(prev => {
                        const newRecipientErrors = [...prev.recipients];
                        newRecipientErrors[recipientIndex] = {
                            ...newRecipientErrors[recipientIndex],
                            [validationKey]: fieldErrors[validationKey] || ""
                        };
                        return {
                            ...prev,
                            recipients: newRecipientErrors
                        };
                    });
                });
            }
        }
    };

    const handleMessageChange = (value: string) => {
        if (value.length > 500) {
            setErrors(prev => ({
            ...prev,
            message: "Message cannot exceed 500 characters."
            }));
            return;
        }
        setMessage(value);
        validateForm({ message: value }, (fieldErrors) => {
            setErrors(prev => ({
            ...prev,
            message:
                value.length === 500
                ? "You have reached the 500 character limit."
                : fieldErrors.message || ""
            }));
        });
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        if (protectPassword) {
            validateForm({ password: value }, (fieldErrors) => {
                setErrors(prev => ({
                    ...prev,
                    password: fieldErrors.password || ""
                }));
            });
        }
    };

    const handlePasswordProtectChange = (checked: boolean) => {
        setProtectPassword(checked);
        if (checked) {
            validateForm({ password: password }, (fieldErrors) => {
                setErrors(prev => ({
                    ...prev,
                    password: fieldErrors.password || ""
                }));
            });
        } else {
            setPassword('');
            setErrors(prev => ({
                ...prev,
                password: ""
            }));
        }
    };

    const prepareApiData = () => {
        if (props.isUpdateMode) {
            const updateApiData = {
                recipients: recipients.map((recipient, index) => {
                    const existingRecipient = updateData?.Recipients?.find(r =>
                        r.Email === recipient.email || r.OrderNumber === parseInt(recipient.order_number)
                    );
                    return {
                        recipient_id: existingRecipient?.RecipientID || "",
                        order_number: parseInt(recipient.order_number) || index + 1,
                        email: recipient.email,
                        phone_number: recipient.phone_number,
                        name: recipient.name,
                        type: recipient.type,
                        is_invite_sent: recipient.is_invite_sent,
                        is_completed: recipient.is_completed,
                    };
                }),
                message: message,
                settings: {
                    password_protect: protectPassword,
                    password: password,
                    reminder: reminderValue.toLowerCase() === "none"? 0 : parseInt(reminderValue.replace(/\D/g, ""), 10) || 1

                },
                ...(completeInOrder && { complete_in_order: completeInOrder }),
            };
            return updateApiData;
        } else {
            const createApiData = {
                files: [
                    {
                        file_name: fileName,
                        file_type: fileType,
                        value: fileBase64,
                    },
                ],
                recipients: recipients.map((recipient, index) => ({
                    recipient_id: "",
                    order_number: parseInt(recipient.order_number) || index + 1,
                    email: recipient.email,
                    phone_number: recipient.phone_number,
                    name: recipient.name,
                    type: recipient.type,
                    is_invite_sent: false,
                })),
                user_id: Profile.ID,
                user_name: Profile.FullName,
                message: message,
                settings: {
                    password_protect: protectPassword,
                    password: password,
                    reminder: reminderValue.toLowerCase() === "none"? 0 : parseInt(reminderValue.replace(/\D/g, ""), 10) || 1,
                },
                complete_in_order: completeInOrder,
            };
            return createApiData;
        }
    };

    const handleSubmit = () => {
        let isFormValid = true;
        let newErrors = {
            message: "",
            password: "",
            recipients: recipients.map(() => ({
                name: "",
                email: "",
                phonenumber: "",
                otptype: "",
            }))
        };

        validateForm({ message: message }, (fieldErrors) => {
            newErrors.message = fieldErrors.message || "";
            if (fieldErrors.message) isFormValid = false;
        });

        if (protectPassword) {
            validateForm({ password: password }, (fieldErrors) => {
                newErrors.password = fieldErrors.password || "";
                if (fieldErrors.password) isFormValid = false;
            });
        }

        recipients.forEach((recipient, index) => {
            const recipientValidation = {
                name: recipient.name,
                email: recipient.email,
                phonenumber: recipient.phone_number,
                otptype: recipient.type
            };

            validateForm(recipientValidation, (fieldErrors) => {
                newErrors.recipients[index] = {
                    name: fieldErrors.name || "",
                    email: fieldErrors.email || "",
                    phonenumber: fieldErrors.phonenumber || "",
                    otptype: fieldErrors.otptype || ""
                };

                if (fieldErrors.name || fieldErrors.email || fieldErrors.phonenumber || fieldErrors.otptype) {
                    isFormValid = false;
                }
            });
        });
        setErrors(newErrors);

        if (!isFormValid) {
            setToast(ToasterTexts.SECURE_DOC_FORM_VALIDATE, ToasterType.DANGER);
            return;
        }

        if (protectPassword && !password) {
            setToast(ToasterTexts.SECURE_DOC_ENTER_PASSWORD, ToasterType.DANGER);
            return;
        }

        if (!protectPassword && password) {
            setToast(ToasterTexts.SECURE_DOC_PASSWORD_VALIDATE, ToasterType.DANGER);
            return;
        }

        setIsLoading(true)

        const apiData = prepareApiData();
        const apiCall = props.isUpdateMode && props.fileID
            ? SecureDocsService.updateSecureDocs(props.fileID, apiData)
            : SecureDocsService.saveSecureDocs(apiData);

        apiCall
            .then((res) => {
                const apiRecipients = res?.data?.recipients || [];
                const files = res?.data?.files || [];

                const formData: PreservedFormData = {
                    messageText: message,
                    reminderDays: parseInt(reminderValue.replace(/\D/g, "")) || 1,
                    passwordProtect: protectPassword,
                    password: password,
                    completeInOrder: completeInOrder
                };

                if (props.isUpdateMode) {
                    const updatedEmailsData: RecipientFormData[] = recipients.map((recipient, index) => ({
                        recipient_id: apiRecipients.find(r => r.email === recipient.email)?.recipient_id || recipient.id.toString(),
                        email: recipient.email,
                        order_number: parseInt(recipient.order_number) || index + 1,
                        name: recipient.name,
                        phone: recipient.phone_number,
                        type: recipient.type,
                        is_completed: recipient.is_completed,
                        is_invite_sent: recipient.is_invite_sent,
                    }));

                    setToast(ToasterTexts.SECURE_DOC_UPDATED_SUCCESS, ToasterType.SUCCESS);
                    props.nextHandler(updatedEmailsData, props.fileID, formData);
                } else {
                    const fileData = files.map(file => file.file_id);
                    const emailsData: RecipientFormData[] = apiRecipients.map((recipient) => ({
                        recipient_id: recipient.recipient_id,
                        email: recipient.email,
                        order_number: recipient.order_number,
                        name: recipients.find(r => r.email === recipient.email)?.name || "",
                        phone: recipients.find(r => r.email === recipient.email)?.phone_number || "",
                        type: recipients.find(r => r.email === recipient.email)?.type || "Email OTP",
                        is_completed: recipients.find(r => r.email === recipient.email)?.is_completed || false,
                        is_invite_sent: recipients.find(r => r.email === recipient.email)?.is_invite_sent || false,
                    }));

                    setToast(
                        ToasterTexts.SECURE_DOC_ADD_RECIPIENTS_CREATED_SUCCESS,
                        ToasterType.SUCCESS
                    );
                    props.nextHandler(emailsData, fileData, formData);
                }
            })
            .catch((error) => {
                console.error("Error submitting form:", error);
                setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCancel = () => {
        navigate(AppRoutes.FALKON_DOCS);
    };

    if (props.isUpdateMode && isLoadingUpdateData) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="success-page-text pt-2 pl-3 t-align-center color-primary-accent h4-semibold">Loading document for updating...</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="success-page-text pt-2 pl-3 t-align-center color-primary-accent h4-semibold">Loading document for configurations...</p>
            </div>
        );
    }

    return (
        <div className="">
            <div className={`${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"} mx-auto w-auto px-5 py-5 o-y-scroll  ${IsMobileDevice ? 'mobile-recipient-container h-v-80' : 'h-v-70'}`}>
                <div className={`d-flex ${IsMobileDevice ? 'flex-direction' : 'justify-between align-center'} pb-3`}>
                    <div className={` h4-semibold t-align-left color-primary-accent ${IsMobileDevice ? 'mb-3 t-align-left' : ''}`}>
                        {props.isUpdateMode
                            ? "Update the agreement details and recipients."
                            : "Send an agreement to others for re-signing, approval or other processing."
                        }
                    </div>

                    <div className={`secure-doc-agreement-options d-flex align-center ${IsMobileDevice ? 'flex-direction t-align-left' : ''}`}>
                        <label className={`checkbox-label ${IsMobileDevice ? 'mb-1' : ''}`}>
                            <Checkbox
                                size="small"
                                checked={completeInOrder}
                                value={completeInOrder}
                                onChange={(e: any) => setCompleteInOrder(e.target.checked)}
                                disabled={props.isUpdateMode}
                                inputProps={
                                    {
                                        tabIndex: 3,
                                        "data-arrow-index": 8,
                                        "data-up-arrow-index": 7,
                                        "data-down-arrow-index": 9,
                                        "data-left-arrow-index": 2,
                                        "data-right-arrow-index": 8,
                                    } as any
                                }
                                classes={{ checked: "selected-checkbox" }}
                                name="CompleteInOrder"
                            />
                            <span
                                className={`h5-semibold add-recipient ${props.isUpdateMode ? "color-primary-inactive" : "c-pointer color-primary-accent"} ${IsMobileDevice ? "mt-5" : ""}`}
                            >
                                Complete in order
                            </span>
                        </label>
                        <div className={` ${IsMobileDevice ? "t-align-left w-p-100" : ""}`}>
                            <button
                                className={` add-recipient add-recipient-border d-flex align-center ${recipients.length >= maxRecipients
                                    ? "color-primary-inactive disabled"
                                    : "c-pointer color-primary-light"
                                    }`}
                                onClick={handleAddRecipient}
                            >
                                <span className="recipient-plus-icon h1-semibold pr-1" >+</span><div className="h5-semibold">Add another recipient</div> 
                            </button>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} noValidate autoComplete="off">

                    <div className="pb-3">
                        {recipients.map((recipient, index) => (
                            <div key={recipient.id} className={`p-relative mb-1  ${IsMobileDevice ? 'mobile-recipient-wrapper' : ''}`}>
                                <div
                                    className={`recipient-card p-relative mb-1 d-flex  ${IsMobileDevice ? 'mobile-recipient-card mobile-recipient-card-size' : ''}`}
                                    style={{
                                        borderColor: getRecipientColor(index + 1),
                                        boxShadow: `0 4px 12px ${getRecipientColor(index + 1)}22`,
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <div
                                        className="recipient-index d-flex align-center justify-center color-primary-accent h4-bold mt-2"
                                        style={{
                                            backgroundColor: getRecipientColor(index + 1),
                                            boxShadow: `0 2px 8px ${getRecipientColor(index + 1)}44`,
                                        }}
                                    >
                                        {index + 1}
                                    </div>
                                    <div className={`recipient-fields ${IsMobileDevice ? 'mobile-recipient-fields' : ''}`}>
                                        <label className={`d-flex flex-direction color-primary-accent h5-bold  ${IsMobileDevice ? "mb-2 d-block" : ""}`}>
                                            Name*
                                            <div className="pt-1">
                                                <TextField
                                                    name={"RecipientName"}
                                                    InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                                    placeholder="John Doe"
                                                    fullWidth
                                                    size="small"
                                                    disabled={props.isUpdateMode && recipient.is_invite_sent}
                                                    value={recipient.name}
                                                    onChange={(e) =>
                                                        handleRecipientChange(
                                                            recipient.id,
                                                            "name",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&:hover fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                        },
                                                    }}
                                                />
                                                <FormHelperText className={`h6-regular color-primary-danger ${IsMobileDevice ? "t-align-left" : "t-align-right"}`}>
                                                    {errors.recipients[index]?.name}
                                                </FormHelperText>
                                            </div>
                                        </label>
                                        <label className={`d-flex flex-direction color-primary-accent h5-bold  ${IsMobileDevice ? "mb-2 d-block" : ""}`}>
                                            Email*
                                            <div className="pt-1">
                                                <TextField
                                                    name={"RecipientEmail"}
                                                    InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                                    placeholder="johndoe@email.com"
                                                    fullWidth
                                                    size="small"
                                                    autoComplete="nope"
                                                    value={recipient.email}
                                                    disabled={props.isUpdateMode && recipient.is_invite_sent}
                                                    onChange={(e) =>
                                                        handleRecipientChange(
                                                            recipient.id,
                                                            "email",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&:hover fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                        },
                                                    }}
                                                />
                                                <FormHelperText className={`h6-regular color-primary-danger ${IsMobileDevice ? "t-align-left" : "t-align-right"}`}>
                                                    {errors.recipients[index]?.email}
                                                </FormHelperText>
                                            </div>
                                        </label>
                                        <label className={`d-flex flex-direction color-primary-accent h5-bold  ${IsMobileDevice ? "mb-2 d-block" : ""}`}>
                                            Phone Number*
                                            <div className="pt-1">
                                                <TextField
                                                    name={"RecipientPhoneNumber"}
                                                    InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
                                                    placeholder="+123456789987"
                                                    fullWidth
                                                    size="small"
                                                    disabled={props.isUpdateMode && recipient.is_invite_sent}
                                                    value={UtilityService.maskPhoneNumberOnLength(recipient.phone_number, '')}
                                                    onChange={(e) =>
                                                        handleRecipientChange(
                                                            recipient.id, "phone_number", UtilityService.unmaskPhoneNumberWithPlus(e.target.value)
                                                        )
                                                    }
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '&:hover fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: getRecipientColor(index + 1),
                                                            },
                                                        },
                                                    }}
                                                />
                                                <FormHelperText className={`h6-regular color-primary-danger ${IsMobileDevice ? "t-align-left" : "t-align-right"}`}>
                                                    {errors.recipients[index]?.phonenumber}
                                                </FormHelperText>
                                            </div>
                                        </label>
                                        <label className={`d-flex flex-direction color-primary-accent h5-bold  ${IsMobileDevice ? "mb-2 d-block" : ""}`}>
                                            Type*
                                            <div className="pt-1">
                                                <Autocomplete
                                                    size="small"
                                                    className={"w-fill"}
                                                    classes={{
                                                        input: "h4-regular color-text-field-font",
                                                        paper:
                                                            "bg-color-menu-bg box-shadow-none dialogue-shadow",
                                                    }}
                                                    options={otpMethods}
                                                    disabled={props.isUpdateMode && recipient.is_invite_sent}
                                                    value={recipient.type || null}
                                                    onChange={(e, value) =>
                                                        handleRecipientChange(
                                                            recipient.id,
                                                            "type",
                                                            value || ""
                                                        )
                                                    }
                                                    getOptionLabel={(option) => option}
                                                    renderOption={(defaultprops, option: any) => (
                                                        <li {...defaultprops}>
                                                            <span
                                                                className={
                                                                    "c-poinet h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-90"
                                                                }
                                                            >
                                                                {option}
                                                            </span>
                                                        </li>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            size="small"
                                                            variant="outlined"
                                                            placeholder="Select Type"
                                                            name="otp"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    '&:hover fieldset': {
                                                                        borderColor: getRecipientColor(index + 1),
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: getRecipientColor(index + 1),
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <FormHelperText className={`h6-regular color-primary-danger ${IsMobileDevice ? "t-align-left" : "t-align-right"}`}>
                                                    {errors.recipients[index]?.otptype}
                                                </FormHelperText>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                                {(index > 0 || props.isUpdateMode) && (
                                    <button
                                        className="remove-recipient-btn p-absolute bg-color-secondary color-primary-inactive h3-bold c-pointer d-flex justify-center align-center"
                                        disabled={props.isUpdateMode && recipient.is_invite_sent}
                                        onClick={() => handleRemoveRecipient(recipient.id)}
                                        title="Remove recipient"
                                        style={{
                                            backgroundColor: recipient.is_invite_sent ? 'bg-color-secondary-accen' : 'bg-color-secondary',
                                            opacity: recipient.is_invite_sent ? 0.6 : 1,
                                            ...(IsMobileDevice && { top: '150px' })
                                        }}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="pb-5">
                        <hr className="horizontal-line"></hr>
                    </div>

                    <div className={`form-container d-flex flex-wrap p-relative ${IsMobileDevice ? 'mobile-form-container flex-direction' : ''}`} style={IsMobileDevice ? { gap: '30px' } : {}}>
                        <div className={`left-section d-flex flex-direction ${IsMobileDevice ? 'mobile-left-section mb-0 w-p-100' : ''}`}>
                            <div  className="d-flex align-center">
                                <label className="color-primary-accent h4-semibold m-0">
                                    Message
                                </label>
                                <Tooltip title={appNote.FALKON_DOCS_EMAIL_BODY_MESSAGE}>
                                    <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        marginLeft: '0.3rem',
                                    }}
                                    >
                                    <NewInfoCircleIcon />
                                    </span>
                                </Tooltip>
                            </div>
                            <textarea
                                className={`message-input ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"} h4-regular color-text-field-font ${IsMobileDevice ? 'min-h-10 custom-border-box-size w-p-100' : ''}`}
                                placeholder="Please review and sign the document at your earliest convenience."
                                value={message}
                                onChange={(e) => handleMessageChange(e.target.value)}
                                maxLength={500}
                            />
                            <FormHelperText className="h6-regular color-primary-danger t-align-right">
                                {errors.message}
                            </FormHelperText>

                            <label className="d-flex flex-direction color-primary-accent h4-semibold">Uploaded Files*</label>
                            <div className={`upload-box bg-color-primary-text-field ${IsMobileDevice ? 'w-p-100 t-align-center custom-border-box-size p-2' : ''}`}>
                                <div className="file-info"><PDFIcon /> <div>{fileName}</div></div>
                            </div>
                        </div>

                        <div className={`right-section ${IsMobileDevice ? 'mobile-right-section w-p-100 custom-border-box-size' : ''}`} style={IsMobileDevice ? { minWidth: 'unset', } : {}}>
                            <div  className="d-flex align-center">
                                <label className="color-primary-accent h4-semibold m-0">
                                    Settings
                                </label>
                                <Tooltip title={appNote.FALKON_DOCS_SETTINGS_MESSAGE}>
                                    <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        marginLeft: '0.3rem',
                                    }}
                                    >
                                    <NewInfoCircleIcon />
                                    </span>
                                </Tooltip>
                            </div>
                            <div className={`settings-box d-flex flex-direction ${IsDarkMode ? "" : "bg-color-secondary "} h5-regular color-text-field-font ${IsMobileDevice ? 'p-2 h-auto d-flex flex-direction mb-0 custom-border-box-size w-p-100' : ''}`} style={IsMobileDevice ? {
                                minHeight: 'auto',
                                gap: '20px',
                                maxWidth: '100%',
                            } : {}
                            }>
                                <div className={`checkbox-item ${IsMobileDevice ? 'd-flex flex-direction mb-0 custom-border-box-size w-p-100' : ''}`} style={IsMobileDevice ? { gap: '10px', maxWidth: '100%', } : {}}>
                                    <div className={`checkbox-label  ${IsMobileDevice ? 'd-flex align-center w-p-100' : ''}`} style={IsMobileDevice ? { gap: '8px', } : {}}>
                                        <Checkbox
                                            size="small"
                                            checked={protectPassword}
                                            value={protectPassword}
                                            onChange={(e: any) =>
                                                handlePasswordProtectChange(e.target.checked)
                                            }
                                            inputProps={
                                                {
                                                    tabIndex: 3,
                                                    "data-arrow-index": 8,
                                                    "data-up-arrow-index": 7,
                                                    "data-down-arrow-index": 9,
                                                    "data-left-arrow-index": 2,
                                                    "data-right-arrow-index": 8,
                                                } as any
                                            }
                                            classes={{ checked: "selected-checkbox" }}
                                            name="PasswordProtect"
                                            style={IsMobileDevice ? { padding: '4px' } : {}}
                                        />
                                        <label className={`d-flex flex-direction color-primary-accent h5-semibold ${IsMobileDevice ? 'flex-1 m-0' : ''}`} >Password protect</label>
                                    </div>
                                    <TextField
                                        label="Password*"
                                        variant="outlined"
                                        placeholder="******"
                                        fullWidth
                                        size="small"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        disabled={!protectPassword}
                                        id="password"
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => handlePasswordChange(e.target.value)}
                                        className={`password-field ${IsMobileDevice ? 'w-p-100 max-width-100 m-0 custom-border-box-size' : ''} ${!protectPassword ? 'bg-color-secondary-main' : ''}`}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    className="c-pointer"
                                                    onClick={onClickShowPassword}
                                                >
                                                    {showPassword ? <HidePassword /> : <ShowPassword />}
                                                </InputAdornment>
                                            ),
                                            classes: { input: "h4-regular color-text-field-font" },
                                        }}
                                        InputLabelProps={{
                                            className: IsMobileDevice ? 'transform-origin left-0' : ''
                                        }}
                                        inputProps={{
                                            maxLength: 20,
                                            tabIndex: 2,
                                            "data-arrow-index": 7,
                                        }}
                                    />
                                    {errors.password && (
                                        <FormHelperText className={`h6-regular color-primary-danger ${IsMobileDevice ? 'margin-4 t-align-left' : ''}`}>
                                            {errors.password}
                                        </FormHelperText>
                                    )}
                                </div>

                                <div className="">
                                    <hr className="horizontal-line"></hr>
                                </div>

                                <span className="color-secondary-light h6-medium">Notify recipients about unsigned documents using the reminder schedule</span>

                                <div className={`checkbox-item ${IsMobileDevice ? 'd-flex flex-direction mb-0 custom-border-box-size w-p-100' : ''}`} style={IsMobileDevice ? {
                                    gap: '10px',
                                    maxWidth: '100%',
                                } : {}}>
                                    <label className={`d-flex flex-direction color-primary-accent h5-semibold ${IsMobileDevice ? 'm-0 mb-0' : ''}`}>Set Reminder</label>
                                    <Autocomplete
                                        size="small"
                                        className="reminder-autocomplete"
                                        fullWidth
                                        classes={{
                                            input: "h4-regular color-text-field-font",
                                            paper: "bg-color-menu-bg box-shadow-none dialogue-shadow",
                                        }}
                                        style={IsMobileDevice ? {
                                            width: '100%',
                                            maxWidth: '100%',
                                            alignSelf: 'flex-start'
                                        } : {}
                                        }
                                        options={[
                                            "None",
                                            "1 day",
                                            "2 days",
                                            "3 days",
                                            "4 days",
                                            "5 days",
                                            "6 days",
                                            "7 days",
                                        ]}
                                        value={reminderValue}
                                        onChange={(e, value) => setReminderValue(value || "1 day")}
                                        getOptionLabel={(option) => option}
                                        renderOption={(defaultprops, option: any) => (
                                            <li {...defaultprops}>
                                                <span
                                                    className={
                                                        "c-poinet h4-regular color-menu-list-font-color p-2 d-flex justify-between w-auto"
                                                    }
                                                >
                                                    {option}
                                                </span>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                variant="outlined"
                                                placeholder="1 day"
                                                name="reminder"
                                                fullWidth
                                                InputProps={{
                                                    ...params.InputProps,
                                                    className: IsMobileDevice ? 't-align-left' : ''
                                                }}
                                                className={IsMobileDevice ? 'w-p-100 t-align-left custom-border-box-size max-width-100 m-0' : ''}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div className={`action-buttons ${IsMobileDevice ? 'mobile-action-buttons d-flex flex-direction mb-4' : ''}`} style={IsMobileDevice ? {
                    gap: '10px',
                } : {}}>
                    {IsMobileDevice ? (
                        <>
                            <ButtonComponent
                                OnClick={handleSubmit}
                                ButtonLabel={props.isUpdateMode ? buttonLabels.UPDATE || "UPDATE" : buttonLabels.NEXT}
                                ClassName="w-100"
                                Variant={"primary-btn-small"}
                            />
                            <ButtonComponent
                                OnClick={handleCancel}
                                ButtonLabel={buttonLabels.CANCEL}
                                ClassName="w-100"
                                Variant={"secondary-btn-small"}
                            />
                        </>
                    ) : (
                        <>
                            <ButtonComponent
                                OnClick={handleCancel}
                                ButtonLabel={buttonLabels.CANCEL}
                                ClassName=""
                                Variant={"secondary-btn-small"}
                            />
                            <ButtonComponent
                                OnClick={handleSubmit}
                                ButtonLabel={props.isUpdateMode ? buttonLabels.UPDATE || "UPDATE" : buttonLabels.NEXT}
                                ClassName="px-4 mr-1"
                                Variant={"primary-btn-small"}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddRecipientComponent;